import cx from "classnames";
import { IconButton } from "components/IconButton/IconButton";
import { NumberInput } from "components/Inputs/NumberInput/NumberInput";
import type { defaultOptions } from "variables/nodeDefaultOptions";
import type { useProjectTreeContentTranslations } from "hooks/projectTreeHooks";
import type { Node } from "interfaces/schemas";
import React, { memo } from "react";
import * as identify from "utils/treeUtils";
import { EditNodeTypes } from "variables";
import s from "../Styles.module.scss";

type ConfigurationControlsProps = {
  configId: string;
  isPasteMode: boolean;
  handleToggleEditMode: (type: string | boolean) => void;
  isEditMode: boolean | string;
  handleCopyNode: () => void;
  handleDelete: () => void;
  handleUpdateQuantity: (n: number) => void;
  openDownloadModal: () => void;
  options: typeof defaultOptions;
  isNodeOptimistic?: boolean;
  translations: ReturnType<typeof useProjectTreeContentTranslations>;
  data: Node;
  handleToggle3dView: () => void;
  onClickOutside: () => void;
  canDeleteInAddToProjectModal: boolean;
};

export const ConfigurationControls = memo(
  ({
    configId,
    isPasteMode,
    handleToggleEditMode,
    isEditMode,
    handleCopyNode,
    handleDelete,
    handleUpdateQuantity,
    openDownloadModal,
    options,
    isNodeOptimistic,
    translations,
    data,
    handleToggle3dView,
    onClickOutside,
    canDeleteInAddToProjectModal = false,
  }: ConfigurationControlsProps) => (
    <div className="d-flex flex-row align-items-center justify-content-between w-100">
      {!identify.isWizardCommissioningCost(data) &&
        (identify.isAnyProduct(data) || identify.isAnyAccessory(data)) && (
          <Quantity
            isEditMode={isEditMode === EditNodeTypes.QUANTITY}
            handleToggleEditMode={() =>
              !identify.isTroxNetcom(data) &&
              !identify.isProductAccessory(data) &&
              handleToggleEditMode(EditNodeTypes.QUANTITY)
            }
            quantity={data.quantity}
            translations={translations}
            onClickOutside={onClickOutside}
            handleUpdate={handleUpdateQuantity}
            options={options}
          />
        )}
      <div
        className={cx(
          "d-flex ms-2 flex-row align-items-center justify-content-end flex-shrink-0",
          s.borderBetween,
        )}
      >
        {!options.editNodeDisabled &&
          !identify.isWizard(data) &&
          !identify.isTroxNetcom(data) && (
            <IconButton
              testId="editConfigButton"
              id={`editConfig${configId}`}
              icon="pencil-light"
              iconClassName="icon-16"
              onClick={() => handleToggleEditMode(EditNodeTypes.TITLE)}
              className={cx("px-2 border-0", {
                "cursor-not-allowed opacity-20 pe-none": isPasteMode,
              })}
              tooltipText={translations.folder.editNode}
              disabled={isPasteMode}
            />
          )}
        {!options.copyNodeDisabled &&
          !identify.isProductAccessory(data) &&
          !identify.isWizardCommissioningCost(data) && (
            <IconButton
              id={`copyConfig${data.id}`}
              tooltipText={translations.folder.copyNode}
              testId="copyIcon"
              icon="copy-light"
              iconClassName="icon-16"
              onClick={handleCopyNode}
              className={cx("px-2 border-0", {
                "cursor-not-allowed opacity-20 pe-none": isPasteMode,
              })}
              disabled={isPasteMode}
            />
          )}
        {!options.ModelViewDisabled &&
          !identify.isTroxNetcom(data) &&
          !identify.isCustomProduct(data) &&
          !identify.isWizardCommissioningCost(data) && (
            <IconButton
              id={`modelView${data.id}`}
              tooltipText={translations.product.modelView}
              testId="3dViewIcon"
              icon="3-d-symbol-regular"
              iconClassName="icon-16"
              onClick={handleToggle3dView}
              className={cx("px-2 border-0", {
                "cursor-not-allowed opacity-20 pe-none": isPasteMode,
              })}
              disabled={isPasteMode}
            />
          )}
        {!options.downloadModalDisabled &&
          !identify.isWizard(data) &&
          !identify.isTroxNetcom(data) &&
          !identify.isCustomProduct(data) && (
            <IconButton
              id={`downloadModal${data.id}`}
              tooltipText={translations.product.download}
              testId="downloadIcon"
              icon="arrow-down-bracket-light"
              iconClassName="icon-16"
              onClick={openDownloadModal}
              className={cx("px-2 border-0", {
                "cursor-not-allowed opacity-20 pe-none": isPasteMode,
              })}
              disabled={isPasteMode}
            />
          )}
        {!identify.isWizard(data) &&
          !identify.isTroxNetcom(data) &&
          !identify.isProductAccessory(data) &&
          ((!options.productAddingModal && !options.deleteNodeDisabled) ||
            (options.productAddingModal && canDeleteInAddToProjectModal)) && (
            <IconButton
              id={`deleteConfig${data.id}`}
              tooltipText={translations.folder.deleteNode}
              testId="deleteIcon"
              icon="trash-can-light"
              iconClassName="icon-16"
              onClick={handleDelete}
              className={cx("px-2 border-0", {
                "cursor-not-allowed opacity-20 pe-none":
                  (isPasteMode && !options.productAddingModal) ||
                  isNodeOptimistic,
              })}
              disabled={
                (isPasteMode && !options.productAddingModal) || isNodeOptimistic
              }
            />
          )}
      </div>
    </div>
  ),
);

type QuantityProps = {
  options: typeof defaultOptions;
  isEditMode: boolean | string;
  quantity: number;
  handleUpdate: (n: number) => void;
  handleToggleEditMode: (type: string | boolean) => void;
  translations: ReturnType<typeof useProjectTreeContentTranslations>;
  onClickOutside: () => void;
};

const Quantity = ({
  options,
  isEditMode,
  quantity,
  handleUpdate,
  translations,
  handleToggleEditMode,
  onClickOutside,
}: QuantityProps) => {
  if (options.simpleQuantityView) {
    return <>{quantity}</>;
  }

  return (
    <div
      className="d-flex align-items-center gap-1 py-2"
      onClick={() => !isEditMode && handleToggleEditMode(false)}
    >
      <span>{translations.product.productQuantity}:</span>
      <div data-testid="product-quantity-input">
        {isEditMode ? (
          <NumberInput
            initialValue={quantity}
            onSave={handleUpdate}
            onClickOutside={onClickOutside}
            min={1}
            max={9999}
          />
        ) : (
          quantity
        )}
      </div>
    </div>
  );
};
